import { USAGE_WARNING_THRESHOLD } from '@gonfalon/billing/constants';
import { type LimitNames } from '@gonfalon/billing/types';
import { getLimitDisplayName } from '@gonfalon/billing/utils';
import { DateFormat } from '@gonfalon/format/constants';
import { format } from '@gonfalon/format/format';
import { formatNumber } from '@gonfalon/format/formatNumber';
import { fromJS, List, Map, Record } from '@gonfalon/immutable-shim/immutableShim';
import type { CreateFunctionInput, ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';

import { load, save } from '#sources/AccountLocalStorage';
import type { PlanLimits, SubscriptionUsage } from '#utils/billingUtils';
import { computeLimitRatio, getLimitNameForOverageType } from '#utils/billingUtils';

export type DismissedOverages = ImmutableMap<{
  [K in LimitNames]?: string;
}>;

export type Overage = ImmutableMap<{
  key: LimitNames;
  usageOver: string;
  usageType: string;
}>;

export type OveragesType = {
  over: List<Overage>;
  near: List<Overage>;
  canUpdatePlan: boolean;
  resetDate?: string;
  hasSubscription: boolean;
};

export class Overages extends Record<OveragesType>({
  over: List<Overage>(),
  near: List<Overage>(),
  canUpdatePlan: false,
  resetDate: undefined,
  hasSubscription: false,
}) {}

export function createOverages(props?: CreateFunctionInput<Overages>) {
  return props ? new Overages(fromJS(props)) : new Overages();
}

export const computeOverage = ({
  usage,
  limit,
  usageType,
  key,
  usagePeriodEndDate,
  dismissedOverages,
}: {
  usage: number;
  limit: number;
  usageType: string;
  key: LimitNames;
  usagePeriodEndDate?: string;
  dismissedOverages?: DismissedOverages;
}) => {
  const usageLimit = computeLimitRatio(usage, limit);
  if (usageLimit === Infinity) {
    return;
  }

  const metricUsagePercent = Math.round(usageLimit * 100);

  const overageInfo = {
    usageOver: key === 'SeatCount' ? `${usage} / ${limit}` : `${metricUsagePercent}%`,
    usageType,
    key,
  };
  if (usageLimit > 1) {
    return { over: overageInfo };
  }
  if (usageLimit > USAGE_WARNING_THRESHOLD / 100) {
    if (dismissedOverages?.get(key) === usagePeriodEndDate) {
      return;
    }
    if (key !== 'SeatCount') {
      return { near: overageInfo };
    }
  }
};

export const formatResetDate = (d: string | number | Date) => format(new Date(d), DateFormat.MMM_D);

type FindOveragesProps = {
  usages: SubscriptionUsage;
  limits: PlanLimits;
  usagePeriodEndDate?: string;
  dismissedOverages?: DismissedOverages;
  canUpdatePlan: boolean;
  hasSubscription: boolean;
  specificOverageAlerts: string[];
};

export function findOverages({
  usages,
  limits,
  usagePeriodEndDate,
  dismissedOverages,
  canUpdatePlan,
  hasSubscription,
  specificOverageAlerts,
}: FindOveragesProps) {
  let overages = createOverages({
    over: [],
    near: [],
    resetDate: usagePeriodEndDate,
    canUpdatePlan,
    hasSubscription,
  });

  specificOverageAlerts.forEach((ot) => {
    const limitName = getLimitNameForOverageType(ot);
    if (!limitName) {
      return;
    }

    const usage = usages.get(limitName) || 0;
    const planLimit = limits.get(limitName);

    // Every limit the subscription does not set reads as null, and a null limit produces no
    // overage: the usage ratio comes out as Infinity or NaN, which clears every threshold in
    // computeOverage. Skipping it here keeps that outcome without asserting the limit exists.
    if (planLimit === null) {
      return;
    }

    const limit = computeOverage({
      usage,
      limit: planLimit,
      usageType: getLimitDisplayName(limitName, 'plural'),
      key: limitName,
      usagePeriodEndDate,
      dismissedOverages,
    });

    if (limit && limit.over) {
      overages = overages.update('over', (over) => over.push(Map(limit.over)));
    } else if (limit && limit.near) {
      overages = overages.update('near', (near) => near.push(Map(limit.near)));
    }
  });

  return overages;
}

export const formatOverageValue = (usageOver: string) => {
  let formattedUsageOver = '';

  if (usageOver.endsWith('%')) {
    usageOver.slice(0, -1);
    formattedUsageOver = formatNumber(parseInt(usageOver, 10));
    formattedUsageOver += '%';
  } else {
    formattedUsageOver = usageOver;
  }

  return formattedUsageOver;
};

export const saveOveragesToCache = (overages: Overages) => save(load().set('overages', overages));
export const getOveragesFromCache = () => {
  const cached = load().get('overages');
  if (cached) {
    return createOverages(cached);
  }

  return createOverages();
};

export const getDismissedOveragesFromCache = () => load().get('dismissedOverages') || (Map() as DismissedOverages);

export const saveDismissedOveragesToCache = (dismissedOverages: List<Overage>, usagePeriodEndDate: string) =>
  dismissedOverages.forEach((o) =>
    save(load().update('dismissedOverages', (all) => (all || Map()).set(o.get('key'), usagePeriodEndDate)))?.get(
      'dismissedOverages',
    ),
  );
